import { useContext, useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { useNavigate, useOutletContext } from "react-router";
import styled from "styled-components";
import { AppQueryClient } from "../../../api/queryClient";
import AppContext from "../../../AppContext";
import { APP_TITLE } from "../../../config/constant";
import { defaultStatusColors, getOpenHoursSpan, getSpanWithColor } from "../../../utils/CardUtils";
import * as Library from "../../../utils/Library";
import { roleAccesse } from "../../../utils/roleManager";
import SwitchButton from "../../common/buttons/SwitchButton";
import ListComponent from "../../common/lists/ListComponent";
import MainWindowHeader, { CustomSearch } from "../../common/mainApp/mainWindowHeaders/MainWindowHeader";
import FFCsCustomExpandedComponent from "./FFCsCustomExpandedComponent";

// #region SCOPED CSS
const StyledPage = styled.div`
    & {
        overflow-y: hidden;
        height: 100%;
        margin-right: -23px;
        margin-left: -24px;
        padding-left: 24px;
        padding-right: 24px;
    }
`;
// #endregion

export default function FFCManagement() {
    document.title = `${APP_TITLE} - FFCs Management`;

    // #region VARS
    const { MainLayoutProps, layoutType } = useOutletContext();
    const navigations = useSelector(state => state.navigationAccesses);
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [listNumRows, setListNumRows] = useState(50);
    const [onlyAffiliatedBrands, setOnlyAffiliatedBrands] = useState(false);
    const [listData, setListData] = useState([]);

    const [loadingAction, setLoadingAction] = useState(false);
    const [loadingChangeStatus, setLoadingChangeStatus] = useState(false);
    const [expandAllRows, setExpandAllRows] = useState(true);
    const [allRowsExpanded, setAllRowsExpanded] = useState(false);
    const [currentRetailBrand, setCurrentRetailBrand] = useState({ name: "", _id: "" });
    const { userData } = useContext(AppContext);
    const userRole = userData?.role;
    const editAccess = roleAccesse(userRole, "retail brand account admin");

    //---------- Header Filters
    const [searchText, setSearchText] = useState("");
    const [allRetailersList, setAllRetailersList] = useState([]);
    const [filteredRetailers, setFilteredRetailers] = useState([]);
    //-----
    const [allStoresList, setAllStoresList] = useState([]);
    const [filteredStores, setFilteredStores] = useState([]);
    //-----
    const [requestEnabled, setRequestsEnabled] = useState(false);

    const filters = {
        filterRetailBrandIDs: filteredRetailers?.map(item => item?.value),
        filterStoreIDs: filteredStores?.map(item => item?.value),
        searchText: searchText
    };

    const listQueryKey = ["FFCList", filters];

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        setupHeaders(
            props,
            onClickRefreshAction,
            navigations,
            allRetailersList,
            allStoresList,
            onStoresChange,
            onRetailersChange,
            onFiltersLoaded,
            onSearch,
            editAccess
        );
    }, [
        props?.layoutType,
        navigations,
        allRetailersList,
        allStoresList,
        filteredStores,
        filteredRetailers,
        requestEnabled,
        editAccess
    ]); // Setup headers

    useEffect(() => {
        const interval = setInterval(() => {
            onClickRefreshAction();
        }, 180000);

        return () => clearInterval(interval);
    }, []); // Auto refresh every 3 minutes

    useEffect(() => {
        if (!listData?.length || allRowsExpanded) return;

        const interval = setInterval(() => {
            const allRowsExpanded = checkAllRowsAreExpanded();
            if (allRowsExpanded) {
                setAllRowsExpanded(true);
                clearInterval(interval);
            } else {
                expandAllRowsHandler({ setExpandToogle: true });
            }
        }, 200);
    }, [listData]);

    useEffect(() => {
        Library.removeQueries(queryClient, listQueryKey);
    }, []); // Cleanup queries on mount
    // #endregion

    // #region METHODS
    const onSearch = searchText => {
        setSearchText(searchText);
    };

    const onRetailersChange = selection => {
        setFilteredRetailers([selection]);

        const currentRetailBrandID = selection?.value;
        sessionStorage.setItem("currentRetailBrand", currentRetailBrandID || "");
        setCurrentRetailBrand({ name: selection?.name, _id: currentRetailBrandID });
    };

    const onStoresChange = (selection, dropdownDataWithSelections) => {
        setFilteredStores(selection);
        setAllStoresList(dropdownDataWithSelections);
    };

    const onFiltersLoaded = data => {
        const defaultRetailBrand =
            sessionStorage.getItem("currentRetailBrand") || localStorage.getItem("defaultRetailBrand");
        let currentRetailBrandID = null;

        if (data?.retailBrands?.length) {
            const retailBrands = data?.retailBrands?.map((item, index) => {
                const defaultRetailBrandID = localStorage.getItem("defaultRetailBrand");
                let retailBrandName = item?.name;

                if (item?._id === defaultRetailBrandID) {
                    retailBrandName = `${item?.name} (default)`;
                }

                return {
                    name: retailBrandName,
                    value: item?._id,
                    selected: item?._id === defaultRetailBrand
                };
            });

            const defaultRetailBrandObj =
                retailBrands?.find(item => item?.value === defaultRetailBrand) || retailBrands?.[0];

            setAllRetailersList(retailBrands);
            setFilteredRetailers([defaultRetailBrandObj]);
            currentRetailBrandID = defaultRetailBrandObj?.value;

            const brandName = data?.retailBrands?.find(item => item?._id === currentRetailBrandID)?.name;
            setCurrentRetailBrand({ name: brandName, _id: defaultRetailBrandObj?.value });
        }

        if (data?.stores?.length) {
            const stores = data?.stores?.map(item => {
                return {
                    name: item?.name,
                    value: item?._id,
                    selected: true,
                    retailBrandID: item?.retailBrandID
                };
            });

            setAllStoresList(stores);
            setFilteredStores(stores);
        }

        setRequestsEnabled(true);
    };

    const onAcceptForCreatingFFCRequest = async (labelBrandID, storeID) => {
        const res = await Library.makeCommonPostRequest(
            "FulfillmentCenters",
            "createFulfillmentCenter",
            { labelBrandID, storeID },
            false,
            setLoadingAction
        );
        const data = res?.data;
        return data;
    };

    const onDeleteRequest = async (fulfillmentCenterID, storeID, labelBrandID) => {
        const res = await Library.makeCommonPostRequest(
            "FulfillmentCenters",
            "updateFulfillmentCenterStatus",
            { fulfillmentCenterID, status: "Removed", storeID, labelBrandID },
            false,
            setLoadingAction
        );
        const data = res?.data;
        if (data?.status !== 200) return false;

        return true;
    };

    const onChangeStatusRequest = async (fulfillmentCenterID, status, storeID, labelBrandID) => {
        const res = await Library.makeCommonPostRequest(
            "FulfillmentCenters",
            "updateFulfillmentCenterStatus",
            { fulfillmentCenterID, status, storeID, labelBrandID },
            false,
            setLoadingChangeStatus
        );
        const data = res?.data;
        if (data?.status !== 200) return false;
        return true;
    };

    const onClickRefreshAction = () => {
        AppQueryClient.invalidateQueries(listQueryKey);
    };

    const handleOnAcceptClick = async (row, isExpandedRow) => {
        const isParentRow = !isExpandedRow;

        const storeID = row?.parentID;
        const brandObjID = row?.labelBrandObjID;
        const fulfillmentCenterID = row?.fulfillmentCenterID;
        const statusBeforeChange = row?.status;
        let replaceFulfillmentCenterID = undefined;
        let fulfillmentCenterStatus = "Fulfilling";

        if (isParentRow) {
            navigate(`/stores/edit/${row?.ourID}`);
            return;
        } else {
            await changeListExpandedRowStatus(storeID, brandObjID, "Connecting", 0);

            if (statusBeforeChange === "Not active") {
                //This is when it will use the create fulfillment center instead
                const data = await onAcceptForCreatingFFCRequest(brandObjID, storeID);
                const success = data?.status === 200;

                if (!success) {
                    onClickRefreshAction();
                    return;
                }
                replaceFulfillmentCenterID = data?.data?.fulfillmentCenterData?._id;
                fulfillmentCenterStatus = "Fulfilling";
            } else {
                await changeListExpandedRowStatus(storeID, brandObjID, "Active", 0, false, fulfillmentCenterID);
                changeListExpandedRowStatus(storeID, brandObjID, "Fulfilling", 0);
            }

            await changeListExpandedRowStatus(storeID, brandObjID, "Fulfilling", -1);
            updateActiveFFCs(true);

            changeListExpandedRowStatus(
                storeID,
                brandObjID,
                fulfillmentCenterStatus,
                0,
                true,
                undefined,
                replaceFulfillmentCenterID
            );
        }
    };

    const handleOnSuspendClick = async (row, isExpandedRow) => {
        const isParentRow = !isExpandedRow;

        const storeID = row?.parentID;
        const brandObjID = row?.labelBrandObjID;
        const fulfillmentCenterID = row?.fulfillmentCenterID;

        if (isParentRow) {
            return;
        } else {
            await changeListExpandedRowStatus(storeID, brandObjID, "Suspending", 0);

            await changeListExpandedRowStatus(storeID, brandObjID, "Suspended", 1, false, fulfillmentCenterID);
            updateActiveFFCs(false);
        }
    };

    const handleOnRemoveClick = async (row, isExpandedRow) => {
        const isParentRow = !isExpandedRow;
        const storeID = row?.parentID;
        const brandObjID = row?.labelBrandObjID;
        const fulfillmentCenterID = row?.fulfillmentCenterID;
        const statusBeforeChange = row?.status;

        if (isParentRow) {
            return;
        } else {
            setLoadingAction(true);

            await changeListExpandedRowStatus(storeID, brandObjID, "Removing", 0);

            const success = await onDeleteRequest(fulfillmentCenterID, storeID, brandObjID);

            if (!success) {
                //await changeListExpandedRowStatus(storeID, brandObjID, "Not active", 0);
                onClickRefreshAction();
                return;
            }

            await changeListExpandedRowStatus(storeID, brandObjID, "Removed", 1);
            if (statusBeforeChange !== "Suspended") updateActiveFFCs(false);
        }
    };

    const updateActiveFFCs = async isIncrement => {
        const activeFFCs = parseInt(localStorage.getItem("activeFFCenters") || 0);
        localStorage.setItem("activeFFCenters", isIncrement ? activeFFCs + 1 : activeFFCs - 1);
    };

    const changeListExpandedRowStatus = async (
        storeID,
        brandObjID,
        newStatus,
        incrementInvitationsAvailable = 0,
        changesOnlyLocal = true,
        fulfillmentCenterID,
        replaceFulfillmentCenterID = undefined
    ) => {
        if (!changesOnlyLocal) {
            if (!fulfillmentCenterID) {
                Library.showToastMessage({
                    type: "error",
                    title: "Change status error",
                    message: "Fulfillment Center ID is required to make the change"
                });
                onClickRefreshAction();
                return;
            }
            const success = await onChangeStatusRequest(fulfillmentCenterID, newStatus, storeID, brandObjID);
            if (!success) {
                onClickRefreshAction();
                return;
            }
        }

        const tmp = listData.map(parentRow => {
            let numActiveFFCs = 0;
            if (parentRow?._id === storeID) {
                parentRow.connectedBrands = parentRow.connectedBrands.map(brand => {
                    if (brand?.labelBrandObjID === brandObjID) {
                        brand.status = newStatus;
                        brand.invitationsAvailable += incrementInvitationsAvailable;
                        brand.numberOfFFCs = brand.numberOfFFCs - incrementInvitationsAvailable;
                        if (replaceFulfillmentCenterID) brand.fulfillmentCenterID = replaceFulfillmentCenterID;
                    }
                    return brand;
                });
            } else {
                parentRow.connectedBrands = parentRow.connectedBrands.map(brand => {
                    if (brand?.labelBrandObjID === brandObjID) {
                        brand.invitationsAvailable += incrementInvitationsAvailable;
                        brand.numberOfFFCs = brand.numberOfFFCs - incrementInvitationsAvailable;
                    }
                    return brand;
                });
            }

            parentRow.connectedBrands.forEach(brand => {
                numActiveFFCs += brand.status === "Fulfilling" ? 1 : 0;
            });

            parentRow.numActiveFFCs = numActiveFFCs;
            return parentRow;
        });

        //console.log("New List Data", tmp);

        setListData(tmp);
    };

    const expandAllRowsHandler = props => {
        const { setExpandToogle } = props || {};

        const expandToogle = setExpandToogle ?? !expandAllRows;

        try {
            setExpandAllRows(expandToogle);

            // eslint-disable-next-line quotes
            const propToExpand = '[aria-label="Expand Row"]';
            // eslint-disable-next-line quotes
            const propToCollapse = '[aria-label="Collapse Row"]';

            const propToUse = expandToogle ? propToExpand : propToCollapse;

            const buttons = document.querySelectorAll(propToUse);
            buttons.forEach(button => {
                button.click();
            });
        } catch (error) {
            Library.showToastMessage({
                type: "error",
                title: "Expand/Collapse error",
                message: "Error while expanding/collapsing rows"
            });
        }
    };

    const checkAllRowsAreExpanded = () => {
        // eslint-disable-next-line quotes
        const propToExpand = `[aria-label="Expand Row"]`;
        const buttons = document.querySelectorAll(propToExpand);
        return buttons?.length === 0;
    };
    // #endregion

    // #region COMPUTED VALUES
    const isAcceptEnabled = row => {
        if (loadingAction || loadingChangeStatus) return false;
        return true;
    };

    const isSuspendEnabled = row => {
        if (loadingAction || loadingChangeStatus) return false;
        return false; //row?.POSstatus === "Connected";
    };

    const isRemovedEnabled = row => {
        if (loadingAction || loadingChangeStatus) return false;
        return false; //row?.POSstatus === "Connected";
    };

    const acceptButtonTitle = (row, isExpandedRow) => {
        const isParentRow = !isExpandedRow;
        if (isParentRow) return "Edit";
        if (row?.status === "Suspended") return "Resume";
        if (row?.invitationsAvailable === 0) return "Request more";
        return "Accept";
    };

    const suspendButtonTitle = (row, isExpandedRow) => {
        const isParentRow = !isExpandedRow;
        if (isParentRow) return "Suspend";
        return "Suspend";
    };

    const removeButtonTitle = (row, isExpandedRow) => {
        const isParentRow = !isExpandedRow;
        if (isParentRow) return "Remove";
        return "Remove";
    };

    // #endregion

    // #region LIST COMPONENT PROPS
    const defaultSortSettings = {
        sortable: true,
        sortFunction: (a, b) => a?.name?.toLowerCase()?.localeCompare(b?.name?.toLowerCase())
    };

    const listRightCardTopComponent = (
        <SwitchButton
            className="flex justify-end w-100"
            height="17px"
            title="All Brands"
            rightTitle="ACTIVE ONLY"
            onToogleChanged={value => setOnlyAffiliatedBrands(value)}
            alwaysGreen={true}
            style={{ justifyContent: "end" }}
        />
    );

    const listColumns = [
        {
            name: (
                <div className="flex align-items-center">
                    <ExpandButton
                        style={{
                            position: "absolute",
                            left: "-30px",
                            top: "13px",
                            cursor: "pointer",
                            paddingRigth: "20px"
                        }}
                        onClick={expandAllRowsHandler}
                        isExpanded={expandAllRows}
                    />
                    <span style={{ marginLeft: "20px" }}>{"Store Name"}</span>
                </div>
            ),
            selector: row => (
                <span className="" style={{ marginLeft: "20px" }}>
                    {row?.name || "--"}
                </span>
            ),
            width: "262px"
            //...defaultSortSettings
        },
        {
            name: "City, Country",
            selector: row =>
                !row?.city && !row?.country
                    ? "--"
                    : (row?.city || "--") + ", " + (row?.country || row?.countryValue || "--"),
            ...defaultSortSettings,
            width: "12%"
        },
        {
            name: "Pickup",
            selector: row => getSpanWithColor(row?.pickupEnabled, defaultStatusColors),
            width: "4.5%"
        },
        {
            name: "Courier",
            selector: row => getSpanWithColor(row?.courierEnabled, defaultStatusColors),
            width: "4.6%"
        },
        {
            name: "Shipping",
            selector: row => getSpanWithColor(row?.shippingEnabled, defaultStatusColors),
            width: "10.6%"
        },
        {
            name: "Open/Closed",
            selector: row => getOpenHoursSpan(row?.isOpen),
            width: "9.9%"
        },
        {
            name: "Active FFCenters",
            selector: row => row?.numActiveFFCs ?? "--"
        },
        {
            name: "POS System",
            selector: row => row?.POSname || "--"
        },
        {
            name: "POS Status",
            selector: row => getSpanWithColor(row?.POSstatus, defaultStatusColors, "text-gray-100")
        }
    ];

    const listApi = {
        endpoint: "getFulfillmentCentersManagementData",
        formData: {
            companyID: localStorage.getItem("currentCompanyID"),
            //retailBrandID: currentRetailBrand?._id,
            limit: listNumRows,
            onlyAffiliatedBrands,
            ...filters
        },
        onSuccess: data => {
            const tmp = data?.map((row, index) => {
                const POSstatus = row?.POSstatus === "Connected" ? "Connected" : "Not connected";
                const POSconnected = POSstatus === "Connected";
                const storeStatus = POSconnected ? row?.status || "Not active" : "Not fulfilling";

                const result = {
                    ...row,
                    status: storeStatus,
                    POSstatus: POSstatus,

                    connectedBrands: row?.connectedBrands?.map(brand => {
                        const status =
                            storeStatus !== "Active"
                                ? "Not connected"
                                : brand?.fulfillmentCenterCurrentStatus?.status === "Active"
                                  ? "Fulfilling"
                                  : brand?.fulfillmentCenterCurrentStatus?.status || "Not active";

                        return {
                            ...brand,
                            parentID: row?._id,
                            parentName: row?.name,
                            parentPOSstatus: POSstatus,
                            parentStoreStatus: storeStatus,
                            status: status
                        };
                    })
                };

                result.numActiveFFCs =
                    storeStatus === "Active"
                        ? result?.connectedBrands?.filter(
                              connectedBrand => connectedBrand?.fulfillmentCenterCurrentStatus?.status === "Active"
                          ).length
                        : 0;

                return result;
            });

            setListData(tmp);
            return tmp;
        }
    };

    const listActions = {
        includeActions: true,
        editIsVisible: false,
        suspendIsVisible: false,
        removeIsVisible: false,
        extraActions: [
            {
                title: (row, isExpandedRow) => acceptButtonTitle(row, isExpandedRow),
                onClick: (row, isExpandedRow) => handleOnAcceptClick(row, isExpandedRow),
                isDisabled: row => !isAcceptEnabled(row)
            },
            {
                title: (row, isExpandedRow) => suspendButtonTitle(row, isExpandedRow),
                onClick: (row, isExpandedRow) => handleOnSuspendClick(row, isExpandedRow),
                isDisabled: row => !isSuspendEnabled(row)
            },
            {
                title: (row, isExpandedRow) => removeButtonTitle(row, isExpandedRow),
                onClick: (row, isExpandedRow) => handleOnRemoveClick(row, isExpandedRow),
                isDisabled: row => !isRemovedEnabled(row)
            }
        ]
    };

    const listIncludeStatus = {
        width: "150px",
        stickyStatus: false,
        statusFromPath: "status",
        statusColors: defaultStatusColors
    };

    // #endregion

    // #region RENDER
    return (
        <StyledPage>
            <ListComponent
                rightCardTopComponent={listRightCardTopComponent}
                title="Fulfillment Centers"
                columns={listColumns}
                pageLimit={listNumRows}
                selectableRows={false}
                currentPage={1}
                queryKey={listQueryKey}
                isLoading={loadingAction || loadingChangeStatus}
                actions={listActions}
                expandableRows={true}
                extendRowBackgroundsToTableWidth={true}
                //expandedRowsColumns={expandedRowsColumns}
                expandedRowName="connectedBrands"
                //set all rows expanded if expandAllRows is true
                expandableRowExpanded={row => expandAllRows}
                includeStatus={listIncludeStatus}
                api={listApi}
                mappedTempData={listData}
                customExpandedComponent={item =>
                    FFCsCustomExpandedComponent({
                        data: item?.data,
                        onClickRefreshAction,
                        handleOnAcceptClick,
                        handleOnSuspendClick,
                        handleOnRemoveClick
                    })
                }
                requestEnabled={requestEnabled}
                onChangeRowsPerPage={rowsPerPage => setListNumRows(rowsPerPage)}
            />
        </StyledPage>
    );
    // #endregion
}

// #region FUNCTIONS
function setupHeaders(
    props,
    onClickRefreshAction,
    navigations,
    allRetailersList,
    allStoresList,
    onStoresChange,
    onRetailersChange,
    onFiltersLoaded,
    onSearch,
    editAccess
) {
    const createMode = navigations?.navigationAccesses?.fulfillmentCenters?.createModeOnly;

    props.setControlBarLeftBtns([
        { customSearch: <CustomSearch enabled={true} onTextChange={onSearch} placeholder="Search" /> }
    ]);
    props.setControlBarCenterBtns([
        { to: "/ffcenters/management", active: true, disabled: false, title: "MANAGEMENT" },
        { to: "/ffcenters", active: false, disabled: true, title: "FFCENTERS" }

        /*{
            to: "products/stock", active: false, disabled: false, title: "STOCK"
        }*/
    ]);
    props.setControlBarRightBtns([
        { to: "#", active: true, disabled: createMode, title: "View", icon: "eye" },
        {
            to: editAccess ? "/labelbrands/request" : "#",
            active: false,
            disabled: !editAccess,
            title: "Request",
            icon: "mail"
        },
        { to: "#", active: false, disabled: true, title: "Details", icon: "microscope" }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <MainWindowHeader
                key={window.location.pathname}
                visible={true}
                layoutTypeOptions={{
                    gridEnabled: false,
                    listEnabled: true
                }}
                onRefreshClick={onClickRefreshAction}
                firstOrganize={
                    Library.isAdminDebug()
                        ? {
                              visible: true,
                              dropdownData: allRetailersList,
                              onDropdownChange: onRetailersChange,
                              disabled: false,
                              type: "singleSelect"
                          }
                        : {
                              visible: true,
                              disabled: true
                          }
                }
                secondOrganize={{
                    visible: true,
                    dropdownData: allStoresList,
                    onDropdownChange: onStoresChange,
                    type: "multiSelect",
                    multidropdownOptions: {
                        labelAllSelected: "All stores",
                        labelNoneSelected: "--",
                        allItems: allStoresList
                    }
                }}
                pageFilters={{
                    useFilters: true,
                    filters: {
                        useRetailBrands: true,
                        useStores: true
                    },
                    onFiltersLoaded: onFiltersLoaded
                }}
                search={{
                    visible: false
                }}
            />
        )
    });
}

const ExpandButton = props => {
    const { onClick, isExpanded = false, className = "", style = {} } = props;

    return (
        <div onClick={onClick} className={`expand-button ${className}`} style={style}>
            {!isExpanded ? (
                <svg fill="currentColor" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.59 16.34l4.58-4.59-4.58-4.59L10 5.75l6 6-6 6z"></path>
                    <path d="M0-.45h24v24H0z" fill="none"></path>
                </svg>
            ) : (
                <svg fill="currentColor" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.41 7.84L12 12.42l4.59-4.58L18 9.25l-6 6-6-6z"></path>
                    <path d="M0-.75h24v24H0z" fill="none"></path>
                </svg>
            )}
        </div>
    );
};
// #endregion
